import React, { useState, useEffect } from "react";
import {
  Layers,
  GitBranch,
  CheckCircle2,
  ShieldCheck,
  Activity,
  ArrowRight,
  ArrowLeft,
  Cpu,
  Server,
  Network,
  Check,
  Sparkles,
  Key,
  Users,
  Video,
  Zap,
  Clock,
  Eye,
  FileCheck,
  Lock,
  Globe,
  ExternalLink,
} from "lucide-react";
import { Reveal } from "./motion";
import { openConsultationModal } from "./ConsultationModal";

/* =========================================================================
   1. VISUAL STAGE 01: ARCHITECTURAL BLUEPRINT & SYSTEM SPECIFICATION
   ========================================================================= */

function ArchitectureStageGraphic() {
  return (
    <div className="w-full h-full min-h-[300px] sm:min-h-[320px] rounded-xl bg-[#F8F9FB] border border-neutral-200/90 p-5 sm:p-6 relative overflow-hidden flex flex-col justify-between select-none shadow-xs font-sans">
      {/* Background Architectural Grid Lines */}
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="arch-spec-grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#94A3B8" strokeWidth="0.5" opacity="0.15" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#arch-spec-grid)" />
        </svg>
      </div>

      {/* Top Header Console */}
      <div className="flex items-center justify-between text-xs text-neutral-600 relative z-10 border-b border-neutral-200/80 pb-3">
        <div className="flex items-center gap-2 text-neutral-900 font-bold">
          <Layers className="w-4 h-4 text-[#FF5A1F]" />
          <span className="tracking-wider uppercase text-[11px]">System Topology & Architecture Model</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2.5 py-0.5 rounded-full bg-[#FF5A1F]/10 border border-[#FF5A1F]/30 text-[10px] text-[#FF5A1F] font-bold">
            SPECIFICATION LOCKED
          </span>
          <span className="px-2.5 py-0.5 rounded-md bg-white border border-neutral-200 text-[10px] text-neutral-700 font-medium shadow-2xs">
            v1.0 Architecture Plan
          </span>
        </div>
      </div>

      {/* High-Level Tier Nodes */}
      <div className="relative z-10 my-auto py-3">
        <div className="grid grid-cols-3 gap-3 sm:gap-4 items-center">
          {/* Client Experience Tier */}
          <div className="flex flex-col items-center text-center p-3 rounded-xl bg-white border border-neutral-200 shadow-2xs">
            <div className="w-10 h-10 rounded-lg bg-sky-50 border border-sky-200 flex items-center justify-center text-sky-600 mb-2">
              <Cpu className="w-5 h-5" />
            </div>
            <span className="text-xs text-neutral-900 font-bold">Client Tier</span>
            <span className="text-[10px] text-neutral-500 mt-0.5">Web • iOS • Mobile</span>
          </div>

          {/* Core Business Logic Tier */}
          <div className="flex flex-col items-center text-center p-3 rounded-xl bg-white border border-[#FF5A1F]/40 shadow-xs relative">
            <div className="absolute -top-2 px-2 py-0.2 rounded-full bg-[#FF5A1F] text-white text-[8px] font-bold tracking-wider uppercase">
              Core Engine
            </div>
            <div className="w-10 h-10 rounded-lg bg-orange-50 border border-[#FF5A1F]/30 flex items-center justify-center text-[#FF5A1F] mb-2">
              <Network className="w-5 h-5" />
            </div>
            <span className="text-xs text-neutral-900 font-bold">API Gateway</span>
            <span className="text-[10px] text-[#FF5A1F] mt-0.5 font-semibold">Auth & Business Logic</span>
          </div>

          {/* Resilient Data Tier */}
          <div className="flex flex-col items-center text-center p-3 rounded-xl bg-white border border-neutral-200 shadow-2xs">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 mb-2">
              <Server className="w-5 h-5" />
            </div>
            <span className="text-xs text-neutral-900 font-bold">Data Tier</span>
            <span className="text-[10px] text-neutral-500 mt-0.5">PostgreSQL • Events</span>
          </div>
        </div>

        {/* Dynamic Laser Signal Conduit */}
        <div className="relative mt-4 h-1 bg-neutral-200 rounded-full overflow-hidden">
          <div
            className="absolute top-0 bottom-0 w-24 bg-gradient-to-r from-transparent via-sky-500 to-transparent"
            style={{ animation: "specLaser 1.8s linear infinite" }}
          />
          <div
            className="absolute top-0 bottom-0 w-24 bg-gradient-to-r from-transparent via-[#FF5A1F] to-transparent"
            style={{ animation: "specLaser 1.8s linear infinite 0.9s" }}
          />
        </div>
      </div>

      {/* Telemetry Footer */}
      <div className="relative z-10 flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-neutral-200/80 text-[11px] text-neutral-600">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-neutral-800 font-medium">Architecture Review: 100% Client Approved</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-neutral-500">Delivery Risk:</span>
          <span className="text-[#FF5A1F] font-bold">Zero Ambiguity</span>
        </div>
      </div>

      <style>{`
        @keyframes specLaser {
          0% { left: -20%; }
          100% { left: 110%; }
        }
      `}</style>
    </div>
  );
}

/* =========================================================================
   2. VISUAL STAGE 02: SPRINT DELIVERY CONSOLE & LIVE CLIENT PREVIEW
   ========================================================================= */

function SprintDeliveryGraphic() {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 1400);
    return () => clearInterval(timer);
  }, []);

  const sprintItems = [
    {
      title: "Sprint Milestone 02: Core Feature Suite",
      detail: "Commit b49e81f • Authored & code-reviewed by senior team",
      badge: "ON SCHEDULE",
      statusColor: "bg-neutral-100 text-neutral-800 border-neutral-200",
    },
    {
      title: "Automated Test Coverage: 64/64 Suites Passed",
      detail: "Unit, functional, and API contracts automatically verified",
      badge: "VERIFIED",
      statusColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
    },
    {
      title: "Bi-Weekly Executive Video Walkthrough",
      detail: "Senior engineers walk your stakeholders through finished work",
      badge: "COMPLETED",
      statusColor: "bg-sky-50 text-sky-700 border-sky-200",
    },
    {
      title: "Live Staging Preview Link Generated",
      detail: "Directly test new features on your own phone & laptop",
      badge: "ACTIVE PREVIEW",
      statusColor: "bg-amber-50 text-amber-800 border-amber-200",
    },
  ];

  return (
    <div className="w-full h-full min-h-[300px] sm:min-h-[320px] rounded-xl bg-[#F8F9FB] border border-neutral-200/90 p-5 sm:p-6 relative overflow-hidden flex flex-col justify-between select-none shadow-xs font-sans">
      {/* Title Bar */}
      <div className="flex items-center justify-between border-b border-neutral-200/80 pb-3 text-xs text-neutral-600">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          </div>
          <span className="ml-1.5 text-neutral-700 font-semibold text-[11px]">Sprint Delivery Console • Cycle 3 of 4</span>
        </div>
        <div className="flex items-center gap-1.5 text-emerald-700 text-[11px] font-semibold">
          <GitBranch className="w-3.5 h-3.5" />
          <span>Bi-Weekly Milestone Active</span>
        </div>
      </div>

      {/* Sprint Items Feed */}
      <div className="space-y-2 py-2.5">
        {sprintItems.map((item, idx) => (
          <div
            key={idx}
            className={`p-2.5 rounded-lg border transition-all duration-300 flex items-center justify-between ${
              idx <= activeStep
                ? "bg-white border-neutral-200 shadow-2xs opacity-100"
                : "bg-neutral-100/60 border-transparent opacity-40"
            } ${idx === activeStep ? "ring-1 ring-emerald-500/40" : ""}`}
          >
            <div>
              <div className="text-xs font-bold text-neutral-900">{item.title}</div>
              <div className="text-[11px] text-neutral-500 mt-0.5">{item.detail}</div>
            </div>
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded border font-semibold ${item.statusColor}`}>
              {item.badge}
            </span>
          </div>
        ))}
      </div>

      {/* Staging URL Link Footer */}
      <div className="pt-2.5 border-t border-neutral-200/80 flex items-center justify-between text-xs text-neutral-600">
        <div className="flex items-center gap-2">
          <Globe className="w-3.5 h-3.5 text-sky-600" />
          <span className="text-[11px] font-mono text-sky-700 font-semibold bg-sky-50 px-2 py-0.5 rounded border border-sky-200">
            https://staging-client.nv.cloud
          </span>
        </div>
        <span className="text-emerald-700 font-bold flex items-center gap-1 text-[11px]">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
          <span>Sprint 100% on Schedule</span>
        </span>
      </div>
    </div>
  );
}

/* =========================================================================
   3. VISUAL STAGE 03: CONCURRENCY STRESS & SECURITY AUDIT
   ========================================================================= */

function HardeningStageGraphic() {
  return (
    <div className="w-full h-full min-h-[300px] sm:min-h-[320px] rounded-xl bg-[#F8F9FB] border border-neutral-200/90 p-5 sm:p-6 relative overflow-hidden flex flex-col justify-between select-none shadow-xs font-sans">
      {/* Header */}
      <div className="flex items-center justify-between text-xs text-neutral-600 relative z-10 border-b border-neutral-200/80 pb-3">
        <div className="flex items-center gap-1.5 text-neutral-900 font-bold">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          <span className="tracking-wider uppercase text-[11px]">Pre-Launch Security & Load Hardening</span>
        </div>
        <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] text-emerald-700 font-bold">
          SOC-2 & OWASP PASSED
        </span>
      </div>

      {/* Radar & Metrics */}
      <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 gap-4 items-center my-auto py-2">
        {/* Radar Scanner Visual */}
        <div className="flex justify-center">
          <div className="relative w-32 h-32 sm:w-36 sm:h-36 rounded-full border border-neutral-300 bg-white flex items-center justify-center shadow-2xs">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full border border-neutral-200 flex items-center justify-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border border-neutral-200 flex items-center justify-center">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-[0_0_8px_#10B981]" />
              </div>
            </div>

            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-full h-[1px] bg-neutral-200" />
              <div className="h-full w-[1px] bg-neutral-200 absolute" />
            </div>

            <div
              className="absolute inset-0 rounded-full pointer-events-none origin-center"
              style={{
                background:
                  "conic-gradient(from 0deg, rgba(16, 185, 129, 0.25) 0deg, transparent 75deg, transparent 360deg)",
                animation: "radarHardening 2.6s linear infinite",
              }}
            />

            <div className="absolute top-5 right-7 w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
            <div className="absolute bottom-6 left-7 w-1.5 h-1.5 rounded-full bg-[#FF5A1F]" />
          </div>
        </div>

        {/* Telemetry Metrics */}
        <div className="space-y-2">
          <div className="p-2.5 rounded-lg bg-white border border-neutral-200 shadow-2xs">
            <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-semibold">OWASP Security Audit</div>
            <div className="text-xs sm:text-sm font-bold text-emerald-700 mt-0.5 flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 text-emerald-600" />
              <span>0 Vulnerabilities Detected</span>
            </div>
          </div>

          <div className="p-2.5 rounded-lg bg-white border border-neutral-200 shadow-2xs">
            <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-semibold">Concurrency Stress Load</div>
            <div className="text-xs sm:text-sm font-bold text-neutral-900 mt-0.5">
              15,000 req/sec <span className="text-[11px] text-neutral-500 font-normal">sustained</span>
            </div>
          </div>

          <div className="p-2.5 rounded-lg bg-white border border-neutral-200 shadow-2xs">
            <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-semibold">Response & Query Latency</div>
            <div className="text-xs sm:text-sm font-bold text-[#FF5A1F] mt-0.5">
              &lt; 6.2ms <span className="text-[11px] text-neutral-500 font-normal">p99 optimized</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 flex items-center justify-between text-[11px] pt-3 border-t border-neutral-200/80 text-neutral-600">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-neutral-800 font-medium">Independent Security Audit Approved</span>
        </span>
        <span className="text-emerald-700 font-bold">READY FOR PRODUCTION</span>
      </div>

      <style>{`
        @keyframes radarHardening {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

/* =========================================================================
   4. VISUAL STAGE 04: ZERO-DOWNTIME CUTOVER & 100% IP HANDOVER
   ========================================================================= */

function CutoverStageGraphic() {
  return (
    <div className="w-full h-full min-h-[300px] sm:min-h-[320px] rounded-xl bg-[#F8F9FB] border border-neutral-200/90 p-5 sm:p-6 relative overflow-hidden flex flex-col justify-between select-none shadow-xs font-sans">
      {/* Header */}
      <div className="flex items-center justify-between text-xs text-neutral-600 relative z-10 border-b border-neutral-200/80 pb-3">
        <div className="flex items-center gap-1.5 text-neutral-900 font-bold">
          <Activity className="w-4 h-4 text-[#FF5A1F]" />
          <span className="tracking-wider uppercase text-[11px]">Production Cutover & SRE Telemetry</span>
        </div>
        <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] text-emerald-700 font-bold">
          99.99% PRODUCTION SLA
        </span>
      </div>

      {/* Server Clusters */}
      <div className="relative z-10 grid grid-cols-3 gap-3 my-auto py-2">
        {[
          { name: "Cluster A", region: "US-East", traffic: "34%", status: "HEALTHY" },
          { name: "Cluster B", region: "EU-West", traffic: "33%", status: "HEALTHY" },
          { name: "Cluster C", region: "AP-South", traffic: "33%", status: "HEALTHY" },
        ].map((node, nIdx) => (
          <div
            key={nIdx}
            className="rounded-lg border border-neutral-200 bg-white p-2.5 sm:p-3 text-center shadow-2xs"
          >
            <div className="flex items-center justify-center gap-1.5 text-xs text-neutral-900 font-bold">
              <Server className="w-3.5 h-3.5 text-emerald-600" />
              <span>{node.name}</span>
            </div>
            <div className="text-[10px] text-neutral-500 mt-0.5">{node.region}</div>
            <div className="text-[11px] text-neutral-800 mt-0.5 font-semibold">Traffic: {node.traffic}</div>
            <div className="text-[10px] text-emerald-700 font-bold mt-1.5 flex items-center justify-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span>{node.status}</span>
            </div>
          </div>
        ))}
      </div>

      {/* SRE ECG Waveform */}
      <div className="relative h-7 bg-white rounded-lg border border-neutral-200 overflow-hidden flex items-center px-3 my-1 shadow-2xs">
        <svg className="w-full h-full" viewBox="0 0 100 20" preserveAspectRatio="none">
          <path
            d="M 0 10 L 25 10 L 30 2 L 35 18 L 40 10 L 65 10 L 70 2 L 75 18 L 80 10 L 100 10"
            fill="none"
            stroke="#059669"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="opacity-90"
          />
        </svg>
        <span className="absolute right-3 text-[10px] font-mono text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
          SRE TELEMETRY: NOMINAL
        </span>
      </div>

      {/* Footer */}
      <div className="relative z-10 flex items-center justify-between text-[11px] pt-3 border-t border-neutral-200/80 text-neutral-600">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-neutral-800 font-medium">Rolling Canary Deployment Active</span>
        </span>
        <span className="text-[#FF5A1F] font-bold">0 SECONDS DOWNTIME</span>
      </div>
    </div>
  );
}

/* =========================================================================
   MAIN COMPONENT: TOP-TIER IT CONSULTING ENGINEERING LIFECYCLE
   ========================================================================= */

export default function EngineeringProcess({ onOpenConsultation }) {
  const [activeStage, setActiveStage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const STAGES = [
    {
      number: "01",
      timing: "Weeks 1 – 2",
      navTitle: "Discovery & Blueprint",
      phasePill: "Phase 1: Foundations",
      headline: "De-Risking Architecture & Technical Scope",
      executiveSummary:
        "Before writing production code, our principal architects map your system boundaries, data models, and integration interfaces. You receive a concrete technical specification and fixed delivery roadmap - eliminating scope creep and budget surprises.",
      deliverables: [
        "Verified Functional Scope & System Architecture Document",
        "Fixed Milestone Roadmap & Budget Commitment",
        "Formal Client Sign-Off Prior to Sprint Execution",
      ],
      governancePill: "Client Approval Required Before Coding",
      renderGraphic: () => <ArchitectureStageGraphic />,
    },
    {
      number: "02",
      timing: "Weeks 3 – 8",
      navTitle: "Sprint Delivery & Demos",
      phasePill: "Phase 2: Execution",
      headline: "Predictable Bi-Weekly Sprints & Live Previews",
      executiveSummary:
        "We operate in transparent 2-week agile sprints backed by automated testing. At the close of every sprint, your executive team participates in a working video demo and receives private staging links to test newly released features firsthand.",
      deliverables: [
        "Bi-Weekly Executive Demos of Working Features",
        "Private Ephemeral Staging Environments for Direct Review",
        "Direct Slack & Teams Communication with Engineering Leads",
      ],
      governancePill: "Working Software Delivered Every 14 Days",
      renderGraphic: () => <SprintDeliveryGraphic />,
    },
    {
      number: "03",
      timing: "Weeks 9 – 10",
      navTitle: "Hardening & Security",
      phasePill: "Phase 3: Verification",
      headline: "High-Concurrency Stress Testing & Zero-Trust Security",
      executiveSummary:
        "Prior to public release, we simulate peak enterprise traffic loads and conduct rigorous third-party security audits. We profile slow database queries and enforce bank-grade encryption to guarantee rock-solid system stability under pressure.",
      deliverables: [
        "15,000 req/sec High-Traffic Concurrency Benchmarks",
        "Zero-Trust OWASP Security & Vulnerability Certification",
        "Sub-Millisecond Database Optimization & Fast Page Loads",
      ],
      governancePill: "Audit-Signed Security & Performance Certification",
      renderGraphic: () => <HardeningStageGraphic />,
    },
    {
      number: "04",
      timing: "Week 11+",
      navTitle: "Cutover & IP Handover",
      phasePill: "Phase 4: Launch & Autonomy",
      headline: "Zero-Downtime Production Cutover & 100% IP Transfer",
      executiveSummary:
        "We orchestrate a seamless, zero-downtime rolling cutover with automated rollback protection. Your organization retains 100% full intellectual property ownership - source repositories, encryption keys, and runbooks are handed over with zero vendor lock-in.",
      deliverables: [
        "Automated Rolling Production Deployment (0 Downtime)",
        "100% Intellectual Property & Repository Ownership Transfer",
        "24/7 SRE Health Telemetry & Post-Launch Warranty Support",
      ],
      governancePill: "100% Full IP & Repository Transfer Handover",
      renderGraphic: () => <CutoverStageGraphic />,
    },
  ];

  // Auto-advance gently every 7.5s unless paused by interaction
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % STAGES.length);
    }, 7500);
    return () => clearInterval(interval);
  }, [isPaused, STAGES.length]);

  const handleConsultation = () => {
    if (onOpenConsultation) {
      onOpenConsultation("Engineering Process Section");
    } else {
      openConsultationModal("Engineering Process Section");
    }
  };

  const current = STAGES[activeStage];

  return (
    <section
      id="engineering-process"
      className="py-14 sm:py-16 bg-[#F8F9FA] text-[#0B0B0B] relative overflow-hidden border-t border-b border-neutral-200/80"
    >
      {/* Background Architectural Grid */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="consulting-roadmap-grid" width="36" height="36" patternUnits="userSpaceOnUse">
              <path d="M 36 0 L 0 0 0 36" fill="none" stroke="#000000" strokeWidth="0.5" opacity="0.04" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#consulting-roadmap-grid)" />
        </svg>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 relative z-10">
        {/* ===============================================================
            HEADER: PROFESSIONAL ENTERPRISE CONSULTING LIFECYCLE
            =============================================================== */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 sm:mb-10">
          <div>
            <Reveal>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/5 border border-black/10 text-[11px] font-mono uppercase tracking-widest text-neutral-700 mb-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] animate-pulse" />
                <span>ENTERPRISE DELIVERY LIFECYCLE</span>
              </div>
            </Reveal>

            <Reveal delay={0.05}>
              <h2 className="font-display font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B0B0B] leading-tight">
                A Predictable Path to <span className="text-[#FF5A1F]">Production.</span>
              </h2>
            </Reveal>

            <Reveal delay={0.08}>
              <p className="mt-1.5 text-xs sm:text-sm text-neutral-600 font-sans max-w-xl">
                How Neuravolt transforms strategic software requirements into high-performance enterprise systems - structured for transparency, speed, and zero technical risk.
              </p>
            </Reveal>
          </div>

          {/* Right Velocity Badge */}
          <Reveal delay={0.1}>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg bg-white border border-neutral-200/90 text-[11px] font-mono text-neutral-700 shadow-2xs self-start md:self-auto">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
              <span className="font-bold text-neutral-900">STANDARD CYCLE: 8-12 WEEKS</span>
              <span className="text-neutral-300">•</span>
              <span className="text-neutral-500">PRODUCTION READY</span>
            </div>
          </Reveal>
        </div>

        {/* ===============================================================
            CONNECTED 4-PHASE MILESTONE TRACK (CONSULTING ROADMAP)
            =============================================================== */}
        <Reveal delay={0.12}>
          <div
            className="mb-6 relative"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Connecting Continuous Conduit Beam (Desktop) */}
            <div className="hidden lg:block absolute top-[22px] left-[6%] right-[6%] h-[2px] bg-neutral-200 z-0">
              <div
                className="h-full bg-gradient-to-r from-transparent via-[#FF5A1F] to-transparent w-40"
                style={{ animation: "conduitTrack 3.2s linear infinite" }}
              />
            </div>

            {/* 4 Clickable Phase Stations */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4 relative z-10">
              {STAGES.map((s, idx) => {
                const isActive = idx === activeStage;
                return (
                  <button
                    key={s.number}
                    type="button"
                    onClick={() => setActiveStage(idx)}
                    onMouseEnter={() => setActiveStage(idx)}
                    className={`p-3 sm:p-3.5 rounded-xl border text-left transition-all duration-300 cursor-pointer ${
                      isActive
                        ? "bg-white border-[#FF5A1F]/60 shadow-md ring-1 ring-[#FF5A1F]/30 translate-y-[-2px]"
                        : "bg-white/80 hover:bg-white border-neutral-200/80 hover:border-neutral-300 text-neutral-600 hover:text-neutral-900 shadow-2xs"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`w-2 h-2 rounded-full transition-colors ${
                            isActive ? "bg-[#FF5A1F] ring-2 ring-[#FF5A1F]/20" : "bg-neutral-300"
                          }`}
                        />
                        <span
                          className={`font-mono text-xs font-bold ${
                            isActive ? "text-[#FF5A1F]" : "text-neutral-500"
                          }`}
                        >
                          Phase {s.number}
                        </span>
                      </div>
                      <span className="text-[10px] font-mono text-neutral-400">
                        {s.timing}
                      </span>
                    </div>

                    <div
                      className={`font-display text-xs sm:text-sm font-semibold truncate ${
                        isActive ? "text-[#0B0B0B]" : "text-neutral-700"
                      }`}
                    >
                      {s.navTitle}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>

        {/* ===============================================================
            ACTIVE STAGE SHOWCASE (EXECUTIVE SPLIT CARD)
            Left: Consulting Deliverables & Outcomes | Right: Live Visual Console
            =============================================================== */}
        <Reveal delay={0.16}>
          <div
            className="rounded-2xl border border-neutral-200/90 bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden p-5 sm:p-6 lg:p-7 relative"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
              {/* Left Column: Executive Scope & Value */}
              <div className="lg:col-span-5 flex flex-col justify-between h-full">
                <div>
                  {/* Phase Pill & Timing */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#FF5A1F]/10 border border-[#FF5A1F]/30 text-[10px] font-mono font-bold text-[#FF5A1F]">
                      <Sparkles className="w-2.5 h-2.5" />
                      <span>{current.phasePill}</span>
                    </span>
                    <span className="text-[10px] font-mono text-neutral-500 font-medium">
                      Timeline: {current.timing}
                    </span>
                  </div>

                  {/* Headline */}
                  <h3 className="font-display text-xl sm:text-2xl font-bold text-[#0B0B0B] leading-snug mb-2.5">
                    {current.headline}
                  </h3>

                  {/* Executive Summary */}
                  <p className="text-xs sm:text-[13px] text-neutral-600 font-sans leading-relaxed mb-4">
                    {current.executiveSummary}
                  </p>

                  {/* Key Client Deliverables Checklist */}
                  <div className="space-y-1.5 mb-5">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 font-bold block mb-1">
                      Key Client Deliverables:
                    </span>
                    {current.deliverables.map((item, iIdx) => (
                      <div
                        key={iIdx}
                        className="flex items-center gap-2 text-xs text-neutral-800 font-medium"
                      >
                        <div className="w-4 h-4 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-600 flex-shrink-0">
                          <Check className="w-2.5 h-2.5" />
                        </div>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>

                  {/* Governance Assurance Pill */}
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-neutral-100 border border-neutral-200/80 text-[11px] font-mono text-neutral-700 font-semibold">
                    <Lock className="w-3 h-3 text-[#FF5A1F]" />
                    <span>{current.governancePill}</span>
                  </div>
                </div>

                {/* Tactical Step Navigation & Dots */}
                <div className="flex items-center justify-between pt-4 mt-5 border-t border-neutral-100">
                  <div className="flex items-center gap-1.5">
                    {STAGES.map((_, dotIdx) => (
                      <button
                        key={dotIdx}
                        type="button"
                        onClick={() => setActiveStage(dotIdx)}
                        aria-label={`Go to phase ${dotIdx + 1}`}
                        className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                          dotIdx === activeStage
                            ? "w-6 bg-[#FF5A1F]"
                            : "w-1.5 bg-neutral-200 hover:bg-neutral-300"
                        }`}
                      />
                    ))}
                  </div>

                  <span className="text-[10px] font-mono text-neutral-500">
                    Phase {activeStage + 1} of 4 Active
                  </span>
                </div>
              </div>

              {/* Right Column: Visual Console Graphic */}
              <div className="lg:col-span-7 h-full flex items-center">
                <div className="w-full transition-all duration-500">
                  {current.renderGraphic()}
                </div>
              </div>
            </div>

            {/* Bottom Enterprise Commitment Strip */}
            <div className="mt-5 pt-4 border-t border-neutral-200/80 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2 text-neutral-600 text-center sm:text-left">
                <Key className="w-3.5 h-3.5 text-[#FF5A1F] flex-shrink-0" />
                <span>
                  Every engagement includes 100% full intellectual property transfer, source code handover, and zero vendor lock-in.
                </span>
              </div>
              <button
                type="button"
                onClick={handleConsultation}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#0B0B0B] hover:bg-[#FF5A1F] text-white text-xs font-semibold transition-all cursor-pointer whitespace-nowrap shadow-xs hover:shadow-md"
              >
                <span>Schedule Architecture Scoping</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>

      <style>{`
        @keyframes conduitTrack {
          0% { left: -10%; }
          100% { left: 105%; }
        }
      `}</style>
    </section>
  );
}
